"use client";

import { Button } from "@/components/ui/button";
import { getDomainFromWindow } from "@/lib/utils";
import { StytchB2B } from "@stytch/nextjs/b2b";
import { B2BProducts } from "@stytch/vanilla-js";
import { useCallback, useEffect, useMemo, useState } from "react";
import { mutate } from "swr";
import { profileURL } from "../../hooks/use-profile";
import { reportAuthError } from "../../lib/analytics";
import { AUTH_CONFIG } from "../config";
import { makeStytchB2BConfigIframe } from "./stytch-config";
import { makeStytchB2BStyle } from "./stytch-style";
import { useAuthCompletion } from "./use-auth-completion";
import { useAuthEventHandler } from "./use-auth-event-handler";

import Icon from "@/components/ui/icon";
import "./stytch-auth.css";
import { TermsAndConditions } from "./terms-and-conditions";

// Used for old chat site, due to handling iframes.
export const B2BAuthFormChat = ({
  loginTitle,
  defaultReturnTo,
  hideHeaderText,
}: {
  loginTitle?: string;
  defaultReturnTo?: string;
  hideHeaderText?: boolean;
}) => {
  const domain = getDomainFromWindow() || "";
  const authenticateUrl = AUTH_CONFIG.getLoginRedirectUrl(domain);
  const autologinBase = `${domain}/authenticate/login/autologin`;

  // Detect iframe
  const [isIframe, setIsIframe] = useState<boolean>(false);
  useEffect(() => {
    try {
      setIsIframe(window.self !== window.top);
    } catch (_e) {
      setIsIframe(true);
    }
  }, []);

  // When embedded in an iframe, listen for completion messages from popup
  useEffect(() => {
    if (!isIframe) {
      return;
    }
    const handler = (e: MessageEvent) => {
      // Accept from same-origin or trusted subdomains; if cross-origin, we still only react to our known shape
      const data = (e?.data ?? {}) as { type?: string };
      if (data && data.type === "b2b-auth-complete") {
        // refresh profile/auth state in this window
        mutate(profileURL);
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [isIframe]);

  const { onAuthComplete } = useAuthCompletion(defaultReturnTo || "/");

  const { handleAuthEvent, isProcessing } = useAuthEventHandler(onAuthComplete);

  // Build Stytch UI config; hide OAuth buttons when in iframe so we can trigger via custom popups
  // Only include Google OAuth and email magic links for chat
  const STYTCH_B2B_CONFIG = useMemo(() => {
    const base = makeStytchB2BConfigIframe(authenticateUrl);
    if (isIframe) {
      return {
        ...base,
        products: [B2BProducts.emailMagicLinks],
      };
    }
    // For non-iframe, limit to Google OAuth and email magic links only
    return base;
  }, [authenticateUrl, isIframe]);

  const STYTCH_B2B_STYLE = useMemo(
    () => makeStytchB2BStyle(hideHeaderText),
    [hideHeaderText],
  );

  // Auto-start moved to /authenticate/login/autologin

  // Helper to open popup flows from an iframe parent (only Google for chat)
  const openPopup = useCallback((path: string) => {
    try {
      const returnTo = window.location.toString();
      window.localStorage.setItem("returnTo", returnTo);
    } catch {
      // ignore storage errors
    }
    const features =
      "width=600,height=700,popup=yes,toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes";
    window.open(path, "groq-auth", features);
  }, []);

  const onGoogleClicked = useCallback(() => {
    openPopup(`${autologinBase}?start=oauth_google`);
  }, [autologinBase, openPopup]);

  return (
    <div className="w-full min-h-36">
      {isProcessing && (
        <div className="mb-3 text-sm text-muted-foreground text-center">
          Creating your organization...
        </div>
      )}

      {/* When embedded in an iframe, render explicit button for Google only */}
      {isIframe && !isProcessing /* && !errorMessage */ && (
        <div className="mb-4 flex flex-col gap-4">
          <div className="flex items-center">
            <div className="border-t border-input grow" />
            <span className="px-4 text-xs uppercase">Continue with</span>
            <div className="border-t border-input grow" />
          </div>
          <Button
            className="rounded-full mx-1"
            onClick={onGoogleClicked}
            type="button"
            variant="outline"
          >
            <Icon icon="google" className="mr-2" />
            Login with Google
          </Button>
        </div>
      )}

      {isIframe ? (
        <>
          {!(isProcessing /* || errorMessage */) && (
            <StytchB2B
              config={STYTCH_B2B_CONFIG}
              styles={STYTCH_B2B_STYLE}
              callbacks={{
                onEvent: handleAuthEvent,
              }}
            />
          )}
        </>
      ) : (
        <>
          {!(isProcessing /* || errorMessage */) && (
            <div>
              <StytchB2B
                config={STYTCH_B2B_CONFIG}
                styles={STYTCH_B2B_STYLE}
                callbacks={{
                  onEvent: handleAuthEvent,
                  onError: (error) => {
                    reportAuthError(error.message);
                  },
                }}
                strings={{
                  "login.title": loginTitle || "Create Account or Login",
                }}
              />
              <TermsAndConditions />
            </div>
          )}
        </>
      )}
    </div>
  );
};
